/**
 * Workout Types & Body Targets Image Directory
 * High-definition cinematic fitness photography generated for TA Fitness.
 */

export const WORKOUT_TYPE_IMAGES = {
  strength: "/images/workouts/workout_strength.jpg",
  hypertrophy: "/images/workouts/workout_hypertrophy.jpg",
  endurance: "/images/workouts/workout_endurance.jpg",
  fat_loss: "/images/workouts/workout_fat_loss.jpg",
  general_fitness: "/images/workouts/workout_general_fitness.jpg",
  cardio: "/images/workouts/target_cardio.jpg",
};

export const BODY_TARGET_IMAGES = {
  chest: "/images/workouts/target_chest.jpg",
  back: "/images/workouts/target_back.jpg",
  shoulders: "/images/workouts/target_shoulders.jpg",
  neck: "/images/workouts/target_shoulders.jpg",
  arms: "/images/workouts/target_arms.jpg",
  upper_arms: "/images/workouts/target_arms.jpg",
  lower_arms: "/images/workouts/target_arms.jpg",
  biceps: "/images/workouts/target_arms.jpg",
  triceps: "/images/workouts/target_arms.jpg",
  forearms: "/images/workouts/target_arms.jpg",
  legs: "/images/workouts/target_legs.jpg",
  upper_legs: "/images/workouts/target_legs.jpg",
  lower_legs: "/images/workouts/target_legs.jpg",
  quads: "/images/workouts/target_legs.jpg",
  hamstrings: "/images/workouts/target_legs.jpg",
  calves: "/images/workouts/target_legs.jpg",
  glutes: "/images/workouts/target_lower_body.jpg",
  lower_body: "/images/workouts/target_lower_body.jpg",
  core: "/images/workouts/target_core.jpg",
  abs: "/images/workouts/target_core.jpg",
  waist: "/images/workouts/target_core.jpg",
  hips: "/images/workouts/target_core.jpg",
  upper_body: "/images/workouts/target_upper_body.jpg",
  full_body: "/images/workouts/target_full_body.jpg",
  cardio: "/images/workouts/target_cardio.jpg",
};

export const DEFAULT_WORKOUT_IMAGE = "/images/workouts/workout_general_fitness.jpg";

/**
 * Returns the best image URL for a given body target (muscle/body part).
 */
export function getBodyTargetImage(targetKey, fallback = DEFAULT_WORKOUT_IMAGE) {
  if (!targetKey) return fallback;
  const key = String(targetKey).toLowerCase().trim().replace(/[\s-]+/g, "_");
  return BODY_TARGET_IMAGES[key] || fallback;
}

/**
 * Returns the best image URL for a given workout type (goal).
 */
export function getWorkoutTypeImage(typeKey, fallback = DEFAULT_WORKOUT_IMAGE) {
  if (!typeKey) return fallback;
  const key = String(typeKey).toLowerCase().trim().replace(/[\s-]+/g, "_");
  return WORKOUT_TYPE_IMAGES[key] || fallback;
}

/**
 * Resolves the most appropriate image for any workout plan, template, or exercise.
 */
export function getWorkoutImage(plan, fallback = DEFAULT_WORKOUT_IMAGE) {
  if (!plan) return fallback;

  // 1. Explicit custom user upload only (ignore old base44 stock diagrams or unsplash placeholders)
  const isCustomUserUpload = (url) =>
    url &&
    typeof url === "string" &&
    !url.includes("base44.com") &&
    !url.includes("Untitleddesign") &&
    !url.includes("unsplash.com") &&
    !url.includes("placeholder") &&
    !url.includes("undefined");

  if (isCustomUserUpload(plan.image_url)) return plan.image_url;
  if (isCustomUserUpload(plan.cover_image_url)) return plan.cover_image_url;

  // 2. Body Part Category match (chest, back, shoulders, arms, legs, core, upper_body, lower_body, etc.)
  const bodyPart = plan.body_part_category || plan.body_part || plan.category || plan.target_muscle;
  if (bodyPart) {
    const key = String(bodyPart).toLowerCase().trim().replace(/[\s-]+/g, "_");
    if (BODY_TARGET_IMAGES[key]) return BODY_TARGET_IMAGES[key];
  }

  // 3. Goal / Workout Type match
  const goal = plan.goal || plan.workout_type || plan.type;
  if (goal) {
    const key = String(goal).toLowerCase().trim().replace(/[\s-]+/g, "_");
    if (WORKOUT_TYPE_IMAGES[key]) return WORKOUT_TYPE_IMAGES[key];
  }

  // 4. Infer from plan name
  const name = String(plan.name || "").toLowerCase();
  if (name.includes("chest") || name.includes("bench") || name.includes("pec") || name.includes("push")) {
    return BODY_TARGET_IMAGES.chest;
  }
  if (name.includes("back") || name.includes("pull") || name.includes("lat") || name.includes("row")) {
    return BODY_TARGET_IMAGES.back;
  }
  if (name.includes("shoulder") || name.includes("delt") || name.includes("overhead")) {
    return BODY_TARGET_IMAGES.shoulders;
  }
  if (name.includes("leg") || name.includes("squat") || name.includes("quad") || name.includes("hamstring")) {
    return BODY_TARGET_IMAGES.legs;
  }
  if (name.includes("arm") || name.includes("bicep") || name.includes("tricep") || name.includes("curl")) {
    return BODY_TARGET_IMAGES.arms;
  }
  if (name.includes("core") || name.includes("ab ") || name.includes("abs") || name.includes("waist")) {
    return BODY_TARGET_IMAGES.core;
  }
  if (name.includes("cardio") || name.includes("hiit") || name.includes("run") || name.includes("endurance")) {
    return BODY_TARGET_IMAGES.cardio;
  }
  if (name.includes("upper")) {
    return BODY_TARGET_IMAGES.upper_body;
  }
  if (name.includes("lower")) {
    return BODY_TARGET_IMAGES.lower_body;
  }
  if (name.includes("full") || name.includes("total")) {
    return BODY_TARGET_IMAGES.full_body;
  }
  if (name.includes("strength") || name.includes("power") || name.includes("heavy")) {
    return WORKOUT_TYPE_IMAGES.strength;
  }
  if (name.includes("hypertrophy") || name.includes("muscle") || name.includes("mass")) {
    return WORKOUT_TYPE_IMAGES.hypertrophy;
  }

  return fallback;
}
